import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useEffect } from "react";
import useAxios from "../api/hooks/useAxios";
import { bootstrapKeys } from "../api/queryKeys";
import { OperationalHeatmapTypes } from "../utils/constants";
import { AxiosContext } from "./AxiosContext";

export const BootstrapContext = createContext({
  isLoading: true,
  isSuccess: false,
  user: null,
  featureFlags: {},
  customerFeatureFlags: {},
  isScanning: false,
  taskId: null,
  onboardingStage: null,
  actionToRole: {},
  MSSPWarningForBreeze: { show_warning: false },
  msspTenants: [],
  inboxNotifications: [],
});

export const BootstrapProvider = ({ children }) => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth0();
  const axiosWithAuth0 = useAxios();
  const { setPlatformUserFromBootstrap } = useContext(AxiosContext);

  const isAuthReady = isAuthenticated && !isAuthLoading;

  const fetchBootstrap = async () => {
    return axiosWithAuth0.get("bootstrap");
  };

  const { data, isLoading, isSuccess, isError } = useQuery({
    queryKey: bootstrapKeys.all,
    queryFn: fetchBootstrap,
    select: (response) => response.data,

    enabled: isAuthReady,
    onSuccess: (responseData) => {
      if (responseData?.user) {
        setPlatformUserFromBootstrap(responseData.user);
      }
    },
  });

  useEffect(() => {
    if (data?.user) {
      setPlatformUserFromBootstrap(data.user);
    }
  }, [data?.user, setPlatformUserFromBootstrap]);

  const envFeatureFlags = data?.feature_flags
    ? {
        remediationsDemoMode:
          data.feature_flags?.enable_remediations_demo_mode || false,
        scenariosDemoMode: data.feature_flags?.scenariosDemoMode || false,
        scenariosSingleMode: data.feature_flags?.scenariosSingleMode || false,
        businessValueMode: data.feature_flags?.businessValueMode || false,
        postureTrendChart: data.feature_flags?.postureTrendChart || false,
        nistControlDemo: data.feature_flags?.nistControlDemo || false,
        isActiveRds: data.feature_flags?.rdsEnabled || false,
      }
    : {};

  const customerFeatureFlagsProcessed = data?.customer_feature_flags
    ? {
        detectionsActive:
          data.customer_feature_flags?.detections_active || false,
        autoRemediationActive:
          data.customer_feature_flags?.auto_remediation_active || false,
        showNightlyFlow:
          data.customer_feature_flags?.show_nightly_flow || false,
        heatmapDisplayMode:
          data.customer_feature_flags?.heatmap_display_mode ||
          OperationalHeatmapTypes.ATTACK_PATH_BASED,
        heatmapOpacityDisabled:
          data.customer_feature_flags?.heatmap_opacity_disabled || false,
        aiPostureDashboardEnabled:
          data.customer_feature_flags?.ai_posture_dashboard_enabled ?? false,
        aiPostureMcpEnabled:
          data.customer_feature_flags?.ai_posture_mcp_enabled ?? false,
      }
    : {};

  const value = {
    isLoading: isLoading || isAuthLoading,
    isSuccess,
    isError,
    user: data?.user || null,
    featureFlags: data?.feature_flags || {},
    envFeatureFlags,
    customerFeatureFlags: data?.customer_feature_flags || {},
    customerFeatureFlagsProcessed,
    isScanning: data?.is_scanning || false,
    taskId: data?.task_id || null,
    onboardingStage: data?.onboarding_stage || null,
    actionToRole: data?.action_to_role || {},
    MSSPWarningForBreeze: data?.mssp_breeze_warning || {
      show_warning: false,
    },
    msspTenants: data?.mssp_tenants || [],
    inboxNotifications: data?.inbox_notifications || [],
  };

  return (
    <BootstrapContext.Provider value={value}>
      {children}
    </BootstrapContext.Provider>
  );
};

export const useBootstrap = () => {
  const context = useContext(BootstrapContext);
  return context;
};
